import { useEffect, useState } from "react";

const POLL_MS = 30_000;
const ENDPOINT = "/.netlify/functions/presence";

/**
 * usePresence — polls the public presence endpoint and returns the current
 * state of Sam's "at my desk" indicator. Stops polling while the tab is
 * hidden (battery / cache friendliness) and re-fetches the moment it
 * becomes visible again.
 *
 * Returns null while the first fetch is in flight; thereafter:
 *   { state: "here"|"around"|"off", label: string,
 *     lastSeenAt: ISO|null, overridden: boolean }
 */
export function usePresence() {
  const [presence, setPresence] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const res = await fetch(ENDPOINT, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPresence(data);
      } catch {
        // Network blip — keep the last known state on screen.
      }
    };

    const schedule = () => {
      if (timer) clearTimeout(timer);
      if (document.visibilityState === "visible") {
        timer = setTimeout(async () => {
          await fetchOnce();
          if (!cancelled) schedule();
        }, POLL_MS);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchOnce().then(() => { if (!cancelled) schedule(); });
      } else if (timer) {
        clearTimeout(timer);
      }
    };

    fetchOnce().then(() => { if (!cancelled) schedule(); });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return presence;
}
